import { getPlacementSpec } from "@/lib/ad-placements";

export interface AdSize {
  width: number;
  height: number;
  flexible: boolean;
  label: string;
  placement?: string;
  placementLabel?: string;
}

export interface CreativeDimensions {
  width: number;
  height: number;
}

export type CreativeSizeRelation = "match" | "larger" | "smaller" | "mismatch";

export function parseAdSize(size: string | null | undefined): AdSize | null {
  const raw = size?.trim();
  if (!raw || raw.toLowerCase() === "native") return null;

  const normalized = raw.replace(/\s+/g, "").replace(/[×✕]/g, "x");
  const flexible = normalized.startsWith("≤") || normalized.startsWith("<=");
  const withoutPrefix = normalized.replace(/^(≤|<=)/, "");
  const match = withoutPrefix.match(/^(\d+)x(\d+)$/i);
  if (!match) return null;

  const width = Number(match[1]);
  const height = Number(match[2]);
  if (!Number.isFinite(width) || !Number.isFinite(height)) return null;

  return {
    width,
    height,
    flexible,
    label: `${flexible ? "≤" : ""}${width}x${height}`,
  };
}

export function getPlacementAdSize(placement: string): AdSize | null {
  const spec = getPlacementSpec(placement);
  const parsed = parseAdSize(spec?.size);
  if (!parsed) return null;

  const width = spec?.maxWidthPx ?? parsed.width;
  return {
    ...parsed,
    width,
    label: `${parsed.flexible ? "≤" : ""}${width}x${parsed.height}`,
    placement,
    placementLabel: spec?.label ?? placement,
  };
}

export function getPlacementAdSizes(placements: string[]): AdSize[] {
  const seen = new Set<string>();
  const sizes: AdSize[] = [];

  for (const placement of placements) {
    if (seen.has(placement)) continue;
    seen.add(placement);
    const size = getPlacementAdSize(placement);
    if (size) sizes.push(size);
  }

  return sizes;
}

export function getSingleAdSizeForPlacements(placements: string[]): AdSize | null {
  const uniqueSizes = new Map<string, AdSize>();
  for (const size of getPlacementAdSizes(placements)) {
    uniqueSizes.set(`${size.flexible ? "max" : "exact"}:${size.width}x${size.height}`, size);
  }
  return uniqueSizes.size === 1 ? Array.from(uniqueSizes.values())[0] : null;
}

export function getAdSlotStyle(placement: string): Record<string, string> {
  const size = getPlacementAdSize(placement);
  if (!size) return {};

  return {
    width: "100%",
    maxWidth: `${size.width}px`,
    aspectRatio: `${size.width} / ${size.height}`,
  };
}

export function formatAdSize(size: Pick<AdSize, "width" | "height" | "flexible">, separator: "x" | "×" = "x") {
  return `${size.flexible ? "≤" : ""}${size.width}${separator}${size.height}`;
}

export function formatCreativeDimensions(dimensions: CreativeDimensions, separator: "x" | "×" = "x") {
  return `${dimensions.width}${separator}${dimensions.height}`;
}

export function compareCreativeSize(
  actual: CreativeDimensions,
  target: Pick<AdSize, "width" | "height">,
): CreativeSizeRelation {
  if (actual.width === target.width && actual.height === target.height) return "match";

  const hasLargerSide = actual.width > target.width || actual.height > target.height;
  const hasSmallerSide = actual.width < target.width || actual.height < target.height;

  if (hasLargerSide && hasSmallerSide) return "mismatch";
  if (hasLargerSide) return "larger";
  if (hasSmallerSide) return "smaller";
  return "mismatch";
}

export function extractHtmlDimensions(html: string | null | undefined): CreativeDimensions | null {
  if (!html) return null;

  const width =
    readDimension(html, /\bwidth\s*=\s*["']?(\d{2,5})(?:px)?["']?/i) ??
    readDimension(html, /\bwidth\s*:\s*(\d{2,5})px/i);
  const height =
    readDimension(html, /\bheight\s*=\s*["']?(\d{2,5})(?:px)?["']?/i) ??
    readDimension(html, /\bheight\s*:\s*(\d{2,5})px/i);

  if (!width || !height) return null;
  return { width, height };
}

function readDimension(value: string, regex: RegExp): number | null {
  const match = value.match(regex);
  if (!match) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}
